import { useCallback, useEffect, useState } from 'react';

import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';

import DateRangeIcon from '@mui/icons-material/DateRange';

import DropdownBody from '../../../components/Dropdown/components/DropdownBody';

import Checkbox from '../../../form/Checkbox';
import Label from '../../../form/Label';

export enum WeekDay {
  Monday = 'MON',
  Tuesday = 'TUE',
  Wednesday = 'WED',
  Thursday = 'THU',
  Friday = 'FRI',
  Saturday = 'SAT',
  Sunday = 'SUN',
}

const getWeekDayLabel = (day: WeekDay) => {
  switch (day) {
    case WeekDay.Monday:
      return 'Segunda-feira';
    case WeekDay.Tuesday:
      return 'Terça-feira';
    case WeekDay.Wednesday:
      return 'Quarta-feira';
    case WeekDay.Thursday:
      return 'Quinta-feira';
    case WeekDay.Friday:
      return 'Sexta-feira';
    case WeekDay.Saturday:
      return 'Sábado';
    case WeekDay.Sunday:
      return 'Domingo';
  }
};

interface MultiSelectWeekDayProps {
  size?: 'condensed' | 'regular';
  selectedDays: WeekDay[];
  onChange: (days: WeekDay[]) => void;
}

const MultiSelectWeekDay = ({ selectedDays, onChange, size = 'regular' }: MultiSelectWeekDayProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedDays || []);

  const hasNotChanged = selectedDays?.length === draft?.length && selectedDays.every((value) => draft?.includes(value));

  const onClick = useCallback(() => {
    setDraft(selectedDays || []);
  }, [selectedDays]);

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const toggleItem = useCallback(
    (dataItem: WeekDay) => {
      const existItem = draft.includes(dataItem);
      const newDraft = existItem ? draft.filter((value) => value !== dataItem) : [...draft, dataItem];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='text' size='condensed' disabled={draft?.length === 0} onClick={handleClear}>
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <DropdownBody className='ml-5'>
            <Dropdown.List
              heightItem={30}
              data={Object.values(WeekDay)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: WeekDay) => (
                <div className='flex gap-2 items-center'>
                  <Checkbox id={item} key={item} checked={draft.includes(item)} onCheckedChange={() => toggleItem(item)} />
                  <Label htmlFor={item}>{getWeekDayLabel(item)}</Label>
                </div>
              )}
            />
          </DropdownBody>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button variant='text' full size='condensed' onClick={handleClose}>
                Cancelar
              </Button>
              <Button size='condensed' full onClick={handleApply} disabled={hasNotChanged}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' startIcon={<DateRangeIcon />}>
        Dias da semana {(selectedDays?.length || 0) > 0 ? `(${selectedDays?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectWeekDay;
